/**
 * EverydayGoal - Web Version
 * Simplified web app without React Navigation (for initial MVP)
 */

import React, { useEffect } from 'react';
import { View, StyleSheet } from 'react-native';
import { GoalProvider } from './contexts/GoalContext';
import { CompletionProvider } from './contexts/CompletionContext';
import { TodayScreen } from './screens/TodayScreen';
import { HistoryScreen } from './screens/HistoryScreen';
import { ReconcileScreen } from './screens/ReconcileScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { AccountListScreen } from './screens/AccountListScreen';
import { GoalListScreen } from './screens/GoalListScreen';
import { CreateAccountScreen } from './screens/CreateAccountScreen';
import { CreateMainGoalScreen } from './screens/CreateMainGoalScreen';
import { EditAccountScreen } from './screens/EditAccountScreen';
import { ExportScreen } from './screens/ExportScreen';
import { AISettingsScreen } from './screens/AISettingsScreen';
import { JourneysOverviewScreen } from './screens/JourneysOverviewScreen';
import { performanceMonitor } from './utils/PerformanceMonitor';

const App: React.FC = () => {
  useEffect(() => {
    // Mark app as ready for launch time tracking
    performanceMonitor.markAppReady();
  }, []);

  // Simple router state for web
  const [currentScreen, setCurrentScreen] = React.useState<string>('Today');
  const [screenParams, setScreenParams] = React.useState<any>({});

  // Map screen names to URL paths
  const getPathForScreen = (screen: string): string => {
    const pathMap: Record<string, string> = {
      Today: '',
      JourneysOverview: 'journeys',
      Welcome: 'welcome',
      CreateAccount: 'create-account',
      CreateMainGoal: 'create-main-goal',
      GoalList: 'goals',
      CreateGoal: 'goals/create',
      AccountList: 'accounts',
      EditAccount: 'accounts/edit',
      History: 'history',
      Reconcile: 'reconcile',
      Settings: 'settings',
      Export: 'settings/export',
      AISettings: 'settings/ai',
    };
    return pathMap[screen] ?? '';
  };

  // Get screen name from URL path
  const getScreenFromPath = (pathname: string): string => {
    const path = pathname.replace(/^\//, ''); // Remove leading slash
    const screenMap: Record<string, string> = {
      '': 'Today',
      'journeys': 'JourneysOverview',
      'welcome': 'Welcome',
      'create-account': 'CreateAccount',
      'create-main-goal': 'CreateMainGoal',
      'goals': 'GoalList',
      'goals/create': 'CreateGoal',
      'accounts': 'AccountList',
      'accounts/edit': 'EditAccount',
      'history': 'History',
      'reconcile': 'Reconcile',
      'settings': 'Settings',
      'settings/export': 'Export',
      'settings/ai': 'AISettings',
    };
    return screenMap[path] ?? 'Today';
  };

  // Update browser URL when screen changes
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      const path = getPathForScreen(currentScreen);
      const newPath = `/${path}`;
      if (window.location.pathname !== newPath) {
        window.history.pushState(null, '', newPath);
      }
    }
  }, [currentScreen]);

  // Handle browser back/forward buttons
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const handlePopState = () => {
      const screenFromPath = getScreenFromPath(window.location.pathname);
      if (screenFromPath !== currentScreen) {
        setCurrentScreen(screenFromPath);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentScreen]);

  // Mock navigation prop
  const mockNavigation = {
    navigate: (screen: string, params?: any) => {
      console.log(`Navigation to ${screen}:`, params);
      setCurrentScreen(screen);
      setScreenParams(params || {});
    },
    replace: (screen: string) => {
      console.log(`Replace with ${screen}`);
      setCurrentScreen(screen);
    },
    goBack: () => {
      console.log('Go back');
      setCurrentScreen('Today');
    },
    addListener: (event: string, callback: () => void) => {
      // Mock listener that returns a cleanup function
      return () => {}; // Unsubscribe function
    },
  } as any;

  const mockRoute = {
    key: currentScreen,
    name: currentScreen as any,
    params: screenParams,
  };

  // Render current screen
  const renderScreen = () => {
    switch (currentScreen) {
      case 'Today':
        return <TodayScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'Today' }} />;
      case 'JourneysOverview':
        return <JourneysOverviewScreen navigation={mockNavigation} />;
      case 'History':
        return <HistoryScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'History', params: screenParams }} />;
      case 'Reconcile':
        return <ReconcileScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'Reconcile', params: screenParams }} />;
      case 'Settings':
        return <SettingsScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'Settings' }} />;
      case 'AccountList':
        return <AccountListScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'AccountList' }} />;
      case 'GoalList':
        return <GoalListScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'GoalList' }} />;
      case 'CreateAccount':
        return <CreateAccountScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'CreateAccount' }} />;
      case 'CreateMainGoal':
        return <CreateMainGoalScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'CreateMainGoal' }} />;
      case 'CreateGoal':
        return <CreateMainGoalScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'CreateGoal', params: screenParams }} />;
      case 'EditAccount':
        return <EditAccountScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'EditAccount', params: screenParams }} />;
      case 'Export':
        return <ExportScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'Export' }} />;
      case 'AISettings':
        return <AISettingsScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'AISettings' }} />;
      default:
        return <TodayScreen navigation={mockNavigation} route={{ ...mockRoute, name: 'Today' }} />;
    }
  };

  return (
    <View style={styles.container}>
      <GoalProvider>
        <CompletionProvider>
          {renderScreen()}
        </CompletionProvider>
      </GoalProvider>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
});

export default App;
